import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { fetchProducts, fetchGoals } from "../lib/api";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const goal = params.get("goal") || "all";
  const [products, setProducts] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGoals().then((d) => setGoals(d.goals));
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProducts(goal === "all" ? undefined : goal).then((p) => {
      setProducts([...p].sort((a, b) => (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0)));
      setLoading(false);
    });
  }, [goal]);

  const tabs = useMemo(() => [{ slug: "all", name: "All Products" }, ...goals], [goals]);
  const active = goals.find((g) => g.slug === goal);

  return (
    <div className="pt-24 md:pt-28">
      {/* header */}
      <div className="border-b border-[#27272A] bg-ink-800/30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
            / The Range
          </span>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl mt-4 leading-none">
            {active ? active.name.toUpperCase() : "ALL PRODUCTS"}
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl leading-relaxed">
            {active ? active.tagline : "The complete clinical-grade research peptide catalogue. Lab tested, Australian dispatched."}
          </p>

          <div className="flex flex-wrap gap-2.5 mt-9">
            {tabs.map((t) => (
              <button
                key={t.slug}
                onClick={() => setParams(t.slug === "all" ? {} : { goal: t.slug })}
                className={`font-mono-tech text-xs uppercase tracking-widest px-4 py-2.5 border transition-colors ${
                  goal === t.slug
                    ? "bg-accent border-accent text-white"
                    : "border-[#27272A] text-zinc-400 hover:border-white hover:text-white"
                }`}
                data-testid={`filter-${t.slug}`}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* grid */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-ink-800 border border-[#27272A] animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24" data-testid="shop-empty">
            <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent mb-4">/ Updating</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">THE RANGE IS BEING UPDATED</h2>
            <p className="text-zinc-400 mt-4 max-w-md mx-auto leading-relaxed text-sm">
              We're refreshing the catalogue right now. Check back shortly — or leave it a spin when we're back.
            </p>
          </div>
        ) : (
          <>
          <p className="font-mono-tech text-xs text-zinc-500 tracking-widest uppercase mb-6">
            {products.length} product{products.length === 1 ? "" : "s"}
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="product-grid">
            {products.map((p, i) => (
              <Reveal key={p.handle} delay={(i % 3) * 0.06}>
                <ProductCard product={p} index={i} />
              </Reveal>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
}
